import React, { useEffect, useState, useCallback } from 'react';
import { Factory, Plus, Trash2, ChevronDown, ChevronRight, Edit3, Save, X } from 'lucide-react';
import api from '../../api';
import { NafCodeGroup } from '../../types';
import Modal from '../../components/Modal';

const NafCodesAdminPage: React.FC = () => {
  const [groups, setGroups] = useState<NafCodeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Create group modal
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [groupFormError, setGroupFormError] = useState('');
  const [groupFormLoading, setGroupFormLoading] = useState(false);

  // Edit group
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editGroupForm, setEditGroupForm] = useState({ name: '', description: '' });
  const [editGroupLoading, setEditGroupLoading] = useState(false);

  // Add item modal
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemGroupId, setAddItemGroupId] = useState<string>('');
  const [itemForm, setItemForm] = useState({ nafCode: '', nafLabel: '' });
  const [itemFormError, setItemFormError] = useState('');
  const [itemFormLoading, setItemFormLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/naf-codes/groups');
      setGroups(res.data.data || res.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des groupes NAF.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleCreateGroup = async () => {
    setGroupFormError('');
    if (!groupForm.name.trim()) {
      setGroupFormError('Le nom est obligatoire.');
      return;
    }
    setGroupFormLoading(true);
    try {
      await api.post('/naf-codes/groups', groupForm);
      setShowCreateGroupModal(false);
      setGroupForm({ name: '', description: '' });
      fetchGroups();
    } catch (err: any) {
      setGroupFormError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setGroupFormLoading(false);
    }
  };

  const handleStartEditGroup = (group: NafCodeGroup) => {
    setEditingGroupId(group.id);
    setEditGroupForm({ name: group.name, description: group.description || '' });
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroupId || !editGroupForm.name.trim()) return;
    setEditGroupLoading(true);
    try {
      await api.patch(`/naf-codes/groups/${editingGroupId}`, editGroupForm);
      setEditingGroupId(null);
      fetchGroups();
    } catch (err) {
      // ignore
    } finally {
      setEditGroupLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm('Supprimer ce groupe NAF ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/naf-codes/groups/${groupId}`);
      fetchGroups();
    } catch (err) {
      // ignore
    }
  };

  const handleOpenAddItem = (groupId: string) => {
    setAddItemGroupId(groupId);
    setItemForm({ nafCode: '', nafLabel: '' });
    setItemFormError('');
    setShowAddItemModal(true);
  };

  const handleAddItem = async () => {
    setItemFormError('');
    if (!itemForm.nafCode.trim()) {
      setItemFormError('Le code NAF est obligatoire.');
      return;
    }
    setItemFormLoading(true);
    try {
      await api.post(`/naf-codes/groups/${addItemGroupId}/items`, itemForm);
      setShowAddItemModal(false);
      setItemForm({ nafCode: '', nafLabel: '' });
      fetchGroups();
    } catch (err: any) {
      setItemFormError(err.response?.data?.message || 'Erreur lors de l\'ajout.');
    } finally {
      setItemFormLoading(false);
    }
  };

  const handleRemoveItem = async (groupId: string, itemId: string) => {
    if (!window.confirm('Retirer ce code NAF du groupe ?')) return;
    try {
      await api.delete(`/naf-codes/groups/${groupId}/items/${itemId}`);
      fetchGroups();
    } catch (err) {
      // ignore
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Factory size={28} /> Groupes de codes NAF</span>
        </h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateGroupModal(true)}>
            <Plus size={16} /> Créer un groupe
          </button>
        </div>
      </div>

      {loading ? (
        <div className="spinner-container"><div className="spinner" /></div>
      ) : error ? (
        <div className="empty-state"><p className="empty-state-title">{error}</p></div>
      ) : groups.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <Factory size={48} />
              <p className="empty-state-title">Aucun groupe NAF</p>
              <p className="empty-state-description">Créez des groupes pour organiser vos codes NAF.</p>
              <button className="btn btn-primary mt-3" onClick={() => setShowCreateGroupModal(true)}>
                <Plus size={16} /> Créer un groupe
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {groups.map((group) => (
            <div key={group.id} className="card">
              <div
                className="card-header cursor-pointer"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="inline-flex">
                  {expandedGroups[group.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  {editingGroupId === group.id ? (
                    <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="form-input"
                        value={editGroupForm.name}
                        onChange={(e) => setEditGroupForm({ ...editGroupForm, name: e.target.value })}
                        style={{ width: '200px', padding: '4px 8px' }}
                      />
                      <input
                        className="form-input"
                        value={editGroupForm.description}
                        onChange={(e) => setEditGroupForm({ ...editGroupForm, description: e.target.value })}
                        placeholder="Description"
                        style={{ width: '250px', padding: '4px 8px' }}
                      />
                      <button className="btn btn-sm btn-primary" onClick={handleSaveEditGroup} disabled={editGroupLoading}>
                        <Save size={14} />
                      </button>
                      <button className="btn btn-sm" onClick={() => setEditingGroupId(null)}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <span className="font-semibold">{group.name}</span>
                      {group.description && (
                        <span className="text-secondary" style={{ marginLeft: '8px', fontSize: '0.85rem' }}>
                          {group.description}
                        </span>
                      )}
                      <span className="badge badge-info" style={{ marginLeft: '8px' }}>
                        {group.items?.length || 0} code{(group.items?.length || 0) > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="btn-group" onClick={(e) => e.stopPropagation()}>
                  <button className="btn btn-sm" onClick={() => handleOpenAddItem(group.id)}>
                    <Plus size={14} /> Ajouter un code
                  </button>
                  <button className="btn btn-sm" onClick={() => handleStartEditGroup(group)}>
                    <Edit3 size={14} />
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteGroup(group.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {expandedGroups[group.id] && (
                <div className="card-body">
                  {(!group.items || group.items.length === 0) ? (
                    <div className="text-secondary text-center" style={{ padding: '16px' }}>
                      Aucun code NAF dans ce groupe
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Code NAF</th>
                            <th>Libellé</th>
                            <th style={{ width: '80px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <code style={{ fontSize: '0.85rem', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>
                                  {item.nafCode}
                                </code>
                              </td>
                              <td>{item.nafLabel || '-'}</td>
                              <td>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleRemoveItem(group.id, item.id)}
                                  title="Retirer"
                                >
                                  <Trash2 size={16} color="#EF4444" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        title="Créer un groupe NAF"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowCreateGroupModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleCreateGroup} disabled={groupFormLoading}>
              {groupFormLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {groupFormError && <div className="login-error">{groupFormError}</div>}
        <div className="form-group">
          <label className="form-label">Nom du groupe *</label>
          <input
            className="form-input"
            value={groupForm.name}
            onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
            placeholder="Ex: Industrie métallurgique"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-textarea"
            value={groupForm.description}
            onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            placeholder="Description du groupe..."
            rows={3}
          />
        </div>
      </Modal>

      {/* Add Item Modal */}
      <Modal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        title="Ajouter un code NAF"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowAddItemModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddItem} disabled={itemFormLoading}>
              {itemFormLoading ? <span className="spinner spinner-sm" /> : 'Ajouter'}
            </button>
          </>
        }
      >
        {itemFormError && <div className="login-error">{itemFormError}</div>}
        <div className="form-group">
          <label className="form-label">Code NAF *</label>
          <input
            className="form-input"
            value={itemForm.nafCode}
            onChange={(e) => setItemForm({ ...itemForm, nafCode: e.target.value })}
            placeholder="Ex: 25.62A"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Libellé</label>
          <input
            className="form-input"
            value={itemForm.nafLabel}
            onChange={(e) => setItemForm({ ...itemForm, nafLabel: e.target.value })}
            placeholder="Ex: Mécanique industrielle"
          />
        </div>
      </Modal>
    </div>
  );
};

export default NafCodesAdminPage;
